import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
    startOfDay, addDays, diffDays, isSameDay, isWeekend, parseDate, toISODate,
    getTaskX, getTaskWidth, getDayLabel, getMonthLabel, formatDateShort, formatDateFull,
    MODE_CONFIG, DEFAULT_STATUS_COLORS, PRIORITY_CONFIG, getAvatarColor, getInitials, isOverdue, t
} from './GanttUtils';

// ─── Dependency Arrow ────────────────────────────────────────────
function DependencyArrow({ fromTask, toTask, timelineStart, mode, rowHeight }) {
    if (!fromTask || !toTask) return null;
    var fromEnd = parseDate(fromTask.end || fromTask.start);
    var toStart = parseDate(toTask.start);
    if (!fromEnd || !toStart) return null;
    var cw = (MODE_CONFIG[mode] || MODE_CONFIG.week).colWidth;

    var fromX = getTaskX(fromEnd, timelineStart, mode) + cw;
    var fromY = fromTask._rowIndex * rowHeight + rowHeight / 2;
    var toX = getTaskX(toStart, timelineStart, mode);
    var toY = toTask._rowIndex * rowHeight + rowHeight / 2;

    var path;
    if (toX < fromX + 8) {
        var midY = Math.max(fromY, toY) + rowHeight * 0.6;
        path = 'M' + fromX + ' ' + fromY + ' H' + (fromX + 8) + ' V' + midY + ' H' + (toX - 8) + ' V' + toY + ' H' + toX;
    } else {
        var midX = fromX + (toX - fromX) / 2;
        path = 'M' + fromX + ' ' + fromY + ' H' + midX + ' V' + toY + ' H' + toX;
    }

    return <path d={path} fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="4,3" markerEnd="url(#gantt-arrow)" />;
}

// ─── Tooltip Popup ───────────────────────────────────────────────
function GanttTooltip({ task, x, y, statusColors, startField, endField, progressField, assigneeField, statusField }) {
    if (!task) return null;
    var name = task.name || '';
    var status = task[statusField || 'status'] || task.status || '';
    var color = statusColors[status] || '#94A3B8';
    var progress = task[progressField || 'progress'] || task.progress || 0;
    var assignee = task[assigneeField || 'assignee'] || task.assignee || '';
    var priority = task.priority || task.task_priority || '';
    var prioConfig = PRIORITY_CONFIG[priority] || null;
    var startDate = parseDate(task[startField || 'start'] || task.start);
    var endDate = parseDate(task[endField || 'end'] || task.end);
    var duration = startDate && endDate ? diffDays(startDate, endDate) : 0;
    var overdue = isOverdue(task[endField || 'end'] || task.end, status);

    return (
        <div className="gantt-tooltip" style={{ left: x, top: y }}>
            <div className="tooltip-title">
                {prioConfig && <span className="tooltip-priority-icon" style={{ color: prioConfig.color }}>{prioConfig.icon} </span>}
                {name}
            </div>
            <div className="tooltip-row">
                <span className="tooltip-label">{t({ vi: 'Trạng thái', en: 'Status', fr: 'Statut', es: 'Estado', id: 'Status', ja: '状態', zh: '状态', ko: '상태', hi: 'स्थिति', ar: 'الحالة' })}</span>
                <span className="tooltip-status" style={{ color: color }}>● {status}</span>
                {overdue && <span className="tooltip-overdue">{t({ vi: 'QUÁ HẠN', en: 'OVERDUE', fr: 'EN RETARD', es: 'VENCIDO', id: 'TERLAMBAT', ja: '期限超過', zh: '已逾期', ko: '기한 초과', hi: 'विलंबित', ar: 'متأخر' })}</span>}
            </div>
            {priority && prioConfig && (
                <div className="tooltip-row">
                    <span className="tooltip-label">{t({ vi: 'Ưu tiên', en: 'Priority', fr: 'Priorité', es: 'Prioridad', id: 'Prioritas', ja: '優先度', zh: '优先级', ko: '우선순위', hi: 'प्राथमिकता', ar: 'الأولوية' })}</span>
                    <span className="tooltip-value" style={{ color: prioConfig.color, fontWeight: 600 }}>{prioConfig.icon} {priority}</span>
                </div>
            )}
            {assignee && (
                <div className="tooltip-row">
                    <span className="tooltip-label">{t({ vi: 'Phụ trách', en: 'Assignee', fr: 'Responsable', es: 'Responsable', id: 'Petugas', ja: '担当', zh: '负责人', ko: '담당', hi: 'प्रभारी', ar: 'المسؤول' })}</span>
                    <span className="tooltip-assignee">
                        <span className="tooltip-avatar" style={{ background: getAvatarColor(assignee) }}>{getInitials(assignee)}</span>
                        {assignee}
                    </span>
                </div>
            )}
            <div className="tooltip-dates-row">
                <span>{startDate ? formatDateFull(startDate) : '—'}</span>
                <span className="tooltip-arrow">→</span>
                <span>{endDate ? formatDateFull(endDate) : '—'}</span>
                {duration > 0 && <span className="tooltip-duration">({duration}{t({ vi: ' ngày', en: 'd', fr: ' j', es: ' d', id: ' hari', ja: '日', zh: '天', ko: '일', hi: ' दिन', ar: ' يوم' })})</span>}
            </div>
            {progress > 0 && (
                <div className="tooltip-progress-row">
                    <div className="tooltip-progress">
                        <div className="tooltip-progress-bar" style={{ width: progress + '%', background: color }} />
                    </div>
                    <span className="tooltip-pct">{progress}%</span>
                </div>
            )}
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════
// ─── MAIN TIMELINE SVG ──────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════
export default function GanttTimeline(props) {
    var {
        tasks, timelineStart, timelineEnd, mode, rowHeight,
        statusColors: statusColorsProp, showProgress, showDependencies, showMilestones, showToday,
        nameField, startField, endField, progressField, statusField, assigneeField, idField,
        onTaskClick, onTaskDoubleClick, onTaskContextMenu, onEmptySlotClick,
        onDragMove, onDragResize, editable, compact
    } = props;

    var statusColors = { ...DEFAULT_STATUS_COLORS, ...statusColorsProp };
    var config = MODE_CONFIG[mode] || MODE_CONFIG.week;
    var totalDays = Math.max(diffDays(timelineStart, timelineEnd) + 1, 1);
    var totalWidth = totalDays * config.colWidth;
    var totalHeight = Math.max(tasks.length * rowHeight, 60);
    var today = startOfDay(new Date());

    // ─── Tooltip state ───────────────────────────────────────────
    var [tooltip, setTooltip] = useState(null);
    var tooltipTimer = useRef(null);

    var showTooltipFor = useCallback(function (task, e) {
        if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
        var rect = e.currentTarget.closest('.gantt-timeline-scroll');
        var bx = rect ? rect.getBoundingClientRect() : { left: 0, top: 0 };
        setTooltip({ task: task, x: e.clientX - bx.left + 12, y: e.clientY - bx.top - 8 });
    }, []);

    var hideTooltip = useCallback(function () {
        tooltipTimer.current = setTimeout(function () { setTooltip(null); }, 150);
    }, []);

    // ─── Drag state ──────────────────────────────────────────────
    var [drag, setDrag] = useState(null);
    var dragRef = useRef(null);

    var handleDragStart = useCallback(function (e, task, dragType) {
        if (!editable) return;
        e.stopPropagation();
        var startX = e.clientX;
        var taskStart = parseDate(task[startField || 'start'] || task.start);
        var taskEnd = parseDate(task[endField || 'end'] || task.end);
        dragRef.current = { task: task, dragType: dragType, startX: startX, origStart: taskStart, origEnd: taskEnd };
        setDrag({ taskId: task.id, dragType: dragType });

        // Khi kéo chỉ vẽ trước vị trí mới; thả chuột mới báo onDragMove / onDragResize MỘT lần
        // (trước đây mỗi lần lệch thêm 1 ngày là gọi cập nhật → nhiều request chồng nhau).
        function onMove(ev) {
            var dx = ev.clientX - startX;
            var daysDelta = Math.round(dx / config.colWidth);
            var ref = dragRef.current;
            if (!ref || daysDelta === ref.daysDelta) return;

            var previewStart = ref.origStart;
            var previewEnd = ref.origEnd;
            if (ref.dragType === 'move') {
                previewStart = addDays(ref.origStart, daysDelta);
                previewEnd = addDays(ref.origEnd, daysDelta);
            } else if (ref.dragType === 'resize-end') {
                var newEnd2 = addDays(ref.origEnd, daysDelta);
                if (newEnd2 < ref.origStart) return;
                previewEnd = newEnd2;
            } else if (ref.dragType === 'resize-start') {
                var newStart2 = addDays(ref.origStart, daysDelta);
                if (newStart2 > ref.origEnd) return;
                previewStart = newStart2;
            }
            ref.daysDelta = daysDelta;
            ref.previewStart = previewStart;
            ref.previewEnd = previewEnd;
            setDrag({ taskId: task.id, dragType: ref.dragType, previewStart: previewStart, previewEnd: previewEnd });
        }

        function onUp() {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            var ref = dragRef.current;
            dragRef.current = null;
            setDrag(null);
            if (!ref || !ref.daysDelta) return;
            if (ref.dragType === 'move') {
                if (onDragMove) onDragMove(task, toISODate(ref.previewStart), toISODate(ref.previewEnd), ref.daysDelta);
            } else if (onDragResize) {
                onDragResize(task, toISODate(ref.previewStart), toISODate(ref.previewEnd), ref.daysDelta);
            }
        }

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [editable, config.colWidth, startField, endField, onDragMove, onDragResize]);

    // ─── Empty slot click ────────────────────────────────────────
    var handleSvgClick = useCallback(function (e) {
        if (!onEmptySlotClick || !editable) return;
        // Only trigger if clicking on grid background (not on a task)
        if (e.target.closest('.gantt-task-bar') || e.target.closest('.gantt-group-bar') || e.target.closest('.gantt-milestone')) return;
        var svgRect = e.currentTarget.getBoundingClientRect();
        var x = e.clientX - svgRect.left + (e.currentTarget.parentElement ? e.currentTarget.parentElement.scrollLeft : 0);
        var y = e.clientY - svgRect.top;
        var dayIndex = Math.floor(x / config.colWidth);
        var rowIndex = Math.floor(y / rowHeight);
        var clickDate = addDays(timelineStart, dayIndex);
        var task = tasks[rowIndex] || null;
        onEmptySlotClick(toISODate(clickDate), task, rowIndex);
    }, [onEmptySlotClick, editable, config.colWidth, rowHeight, timelineStart, tasks]);

    // ─── Build task index map ────────────────────────────────────
    var taskMap = useMemo(function () {
        var map = {};
        tasks.forEach(function (t, i) {
            t._rowIndex = i;
            map[t.id || t[idField || 'id']] = t;
        });
        return map;
    }, [tasks, idField]);

    // ─── Column headers ──────────────────────────────────────────
    var columns = useMemo(function () {
        var cols = [];
        for (var i = 0; i < totalDays; i++) {
            var d = addDays(timelineStart, i);
            cols.push({
                date: d, x: i * config.colWidth,
                isToday: isSameDay(d, today), isWeekend: isWeekend(d),
                isMonthStart: d.getDate() === 1, isWeekStart: d.getDay() === 1
            });
        }
        return cols;
    }, [timelineStart, totalDays, config.colWidth]);

    var monthHeaders = useMemo(function () {
        var headers = [];
        var currentMonth = -1;
        var startX = 0;
        columns.forEach(function (col, i) {
            var m = col.date.getMonth();
            if (m !== currentMonth) {
                if (currentMonth >= 0) {
                    headers.push({ label: getMonthLabel(addDays(timelineStart, startX / config.colWidth)) + ' ' + columns[i - 1].date.getFullYear(), x: startX, width: col.x - startX });
                }
                currentMonth = m;
                startX = col.x;
            }
            if (i === columns.length - 1) {
                headers.push({ label: getMonthLabel(col.date) + ' ' + col.date.getFullYear(), x: startX, width: col.x + config.colWidth - startX });
            }
        });
        return headers;
    }, [columns, config.colWidth, timelineStart]);

    // ─── Render ──────────────────────────────────────────────────
    return (
        <div className="gantt-timeline-wrapper">
            {/* Header */}
            <div className="gantt-timeline-header" style={{ width: totalWidth }}>
                <div className="header-row month-row" style={{ height: rowHeight / 2 }}>
                    {monthHeaders.map(function (h, i) {
                        return <div key={i} className="month-cell" style={{ left: h.x, width: h.width }}>{h.label}</div>;
                    })}
                </div>
                <div className="header-row day-row" style={{ height: rowHeight / 2 }}>
                    {columns.map(function (col, i) {
                        if ((mode === 'quarter' || mode === 'year') && !col.isMonthStart && !col.isWeekStart) return null;
                        return (
                            <div key={i} className={'day-cell' + (col.isToday ? ' today' : '') + (col.isWeekend ? ' weekend' : '')} style={{ left: col.x, width: config.colWidth }}>
                                {mode === 'week' || mode === 'day'
                                    ? <span>{getDayLabel(col.date)} {col.date.getDate()}</span>
                                    : <span>{col.date.getDate()}</span>}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* SVG Body */}
            <svg className="gantt-svg" width={totalWidth} height={totalHeight} onClick={handleSvgClick}>
                <defs>
                    <marker id="gantt-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#94A3B8" />
                    </marker>
                </defs>

                {/* Grid */}
                {columns.map(function (col, i) {
                    return <rect key={i} x={col.x} y={0} width={config.colWidth} height={totalHeight} className={'grid-cell' + (col.isWeekend ? ' weekend' : '') + (col.isToday ? ' today' : '')} />;
                })}
                {tasks.map(function (t, i) {
                    return <line key={i} x1={0} y1={(i + 1) * rowHeight} x2={totalWidth} y2={(i + 1) * rowHeight} className="row-separator" />;
                })}

                {/* Today line */}
                {showToday && (function () {
                    var d = diffDays(timelineStart, today);
                    if (d < 0 || d > totalDays) return null;
                    var tx = d * config.colWidth + config.colWidth / 2;
                    return <line x1={tx} y1={0} x2={tx} y2={totalHeight} stroke="#EF4444" strokeWidth="2" strokeDasharray="6,4" opacity="0.7" />;
                })()}

                {/* Dependencies */}
                {showDependencies && tasks.map(function (task) {
                    var deps = task.dependencies || [];
                    if (!Array.isArray(deps)) deps = typeof deps === 'string' ? deps.split(',').map(function (s) { return s.trim(); }) : [];
                    return deps.map(function (depId) {
                        var fromTask = taskMap[depId];
                        if (!fromTask) return null;
                        return <DependencyArrow key={task.id + '-' + depId} fromTask={fromTask} toTask={task} timelineStart={timelineStart} mode={mode} rowHeight={rowHeight} />;
                    });
                })}

                {/* Task bars */}
                {tasks.map(function (task, i) {
                    var taskStart = parseDate(task[startField || 'start'] || task.start);
                    var taskEnd = parseDate(task[endField || 'end'] || task.end);
                    if (!taskStart) return null;
                    if (drag && drag.taskId === task.id && drag.previewStart) {
                        taskStart = drag.previewStart;
                        taskEnd = drag.previewEnd;
                    }

                    var status = task[statusField || 'status'] || task.status || 'New';
                    var color = statusColors[status] || '#94A3B8';
                    var progress = showProgress ? (task[progressField || 'progress'] || task.progress || 0) : 0;
                    var isMilestone = task.type === 'milestone';
                    var isGroup = task.type === 'group';
                    var isDragging = drag && drag.taskId === task.id;

                    var x = getTaskX(taskStart, timelineStart, mode);
                    var w = getTaskWidth(taskStart, taskEnd || taskStart, mode);
                    var y = i * rowHeight;
                    var barH = compact ? 18 : 34;
                    var barY = y + (rowHeight - barH) / 2;

                    // Milestone
                    if (isMilestone && showMilestones) {
                        var mx = x + config.colWidth / 2;
                        var my = y + rowHeight / 2;
                        var ms = compact ? 6 : 8;
                        return (
                            <g key={task.id} className="gantt-milestone"
                                onClick={function (e) { e.stopPropagation(); if (onTaskClick) onTaskClick(task); }}
                                onDoubleClick={function (e) { e.stopPropagation(); if (onTaskDoubleClick) onTaskDoubleClick(task); }}
                                onContextMenu={function (e) { e.preventDefault(); e.stopPropagation(); if (onTaskContextMenu) onTaskContextMenu(e, task); }}
                                onMouseEnter={function (e) { showTooltipFor(task, e); }}
                                onMouseLeave={hideTooltip}
                            >
                                <rect x={mx - ms} y={my - ms} width={ms * 2} height={ms * 2} fill={color} transform={'rotate(45 ' + mx + ' ' + my + ')'} rx="1" />
                            </g>
                        );
                    }

                    // Group bracket
                    if (isGroup) {
                        return (
                            <g key={task.id} className="gantt-group-bar"
                                onClick={function (e) { e.stopPropagation(); if (onTaskClick) onTaskClick(task); }}
                                onDoubleClick={function (e) { e.stopPropagation(); if (onTaskDoubleClick) onTaskDoubleClick(task); }}
                                onContextMenu={function (e) { e.preventDefault(); e.stopPropagation(); if (onTaskContextMenu) onTaskContextMenu(e, task); }}
                                onMouseEnter={function (e) { showTooltipFor(task, e); }}
                                onMouseLeave={hideTooltip}
                            >
                                <rect x={x} y={barY + barH - 4} width={Math.max(w, 2)} height={4} fill={color} rx="1" />
                                <rect x={x} y={barY + barH - 8} width={3} height={8} fill={color} />
                                <rect x={x + Math.max(w, 2) - 3} y={barY + barH - 8} width={3} height={8} fill={color} />
                                {progress > 0 && w > 30 && (
                                    <text x={x + w / 2} y={barY + barH - 10} textAnchor="middle" className="group-progress-text" fill={color}>{progress}%</text>
                                )}
                            </g>
                        );
                    }

                    // ─── Normal task bar (rich content) ─────────────
                    var progressW = w * (progress / 100);
                    var priority = task.priority || task.task_priority || '';
                    var prioConfig = PRIORITY_CONFIG[priority] || null;
                    var assignee = task[assigneeField || 'assignee'] || task.assignee || '';
                    var taskName = task[nameField || 'name'] || task.name || '';
                    var overdue = isOverdue(task[endField || 'end'] || task.end, status);

                    // Layout thresholds based on bar width
                    var showName = w > 30;
                    var showPriority = w > 50 && prioConfig;
                    var showAssigneeAvatar = w > 80 && assignee;
                    var showProgressBar = w > 60 && showProgress && progress > 0;
                    var showProgressText = w > 100 && showProgress;

                    return (
                        <g key={task.id} className={'gantt-task-bar' + (isDragging ? ' dragging' : '') + (overdue ? ' overdue' : '')}
                            onClick={function (e) { e.stopPropagation(); if (onTaskClick) onTaskClick(task); }}
                            onDoubleClick={function (e) { e.stopPropagation(); if (onTaskDoubleClick) onTaskDoubleClick(task); }}
                            onContextMenu={function (e) { e.preventDefault(); e.stopPropagation(); if (onTaskContextMenu) onTaskContextMenu(e, task); }}
                            onMouseEnter={function (e) { showTooltipFor(task, e); }}
                            onMouseLeave={hideTooltip}
                        >
                            {/* Shadow for depth */}
                            <rect x={x + 1} y={barY + 1} width={Math.max(w, 4)} height={barH} fill="#000" opacity="0.04" rx="4" />
                            {/* Background card */}
                            <rect x={x} y={barY} width={Math.max(w, 4)} height={barH} fill="#fff" rx="4" />
                            {/* Status color left accent */}
                            <rect x={x} y={barY} width={3} height={barH} fill={overdue ? '#EF4444' : color} rx="1.5" />
                            {/* Progress fill (subtle background) */}
                            {progress > 0 && (
                                <rect x={x + 3} y={barY} width={Math.max(progressW - 3, 0)} height={barH} fill={color} opacity="0.1" rx="0 4 4 0" />
                            )}
                            {/* Border */}
                            <rect x={x} y={barY} width={Math.max(w, 4)} height={barH} fill="none"
                                stroke={overdue ? '#EF4444' : color} strokeWidth={overdue ? 1.5 : 0.8} opacity={overdue ? 0.7 : 0.3} rx="4" />

                            {/* Rich content via foreignObject */}
                            {showName && (
                                <foreignObject x={x + 6} y={barY} width={Math.max(w - 10, 20)} height={barH}>
                                    <div xmlns="http://www.w3.org/1999/xhtml" className="bar-content" style={{ height: barH + 'px' }}>
                                        {/* Row 1: Priority icon + Task name */}
                                        <div className="bar-row1">
                                            {showPriority && (
                                                <span className="bar-priority" style={{ color: prioConfig.color }} title={priority}>
                                                    {prioConfig.icon}
                                                </span>
                                            )}
                                            <span className="bar-name">{taskName}</span>
                                        </div>
                                        {/* Row 2: Avatar + Assignee + Progress (only if bar tall enough) */}
                                        {barH >= 28 && (showAssigneeAvatar || showProgressBar) && (
                                            <div className="bar-row2">
                                                {showAssigneeAvatar && (
                                                    <span className="bar-avatar" style={{ background: getAvatarColor(assignee) }}>
                                                        {getInitials(assignee)}
                                                    </span>
                                                )}
                                                {showProgressBar && (
                                                    <span className="bar-progress-wrap">
                                                        <span className="bar-progress-track">
                                                            <span className="bar-progress-fill" style={{ width: progress + '%', background: color }} />
                                                        </span>
                                                        {showProgressText && <span className="bar-progress-pct">{progress}%</span>}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </foreignObject>
                            )}

                            {/* Overdue warning icon */}
                            {overdue && w > 20 && (
                                <g>
                                    <circle cx={x + Math.max(w, 4) - 8} cy={barY + 8} r={6} fill="#EF4444" />
                                    <text x={x + Math.max(w, 4) - 8} y={barY + 8} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize="8" fontWeight="700">!</text>
                                </g>
                            )}

                            {/* Drag handles (only if editable) */}
                            {editable && (
                                <React.Fragment>
                                    <rect x={x + 5} y={barY} width={Math.max(w - 10, 2)} height={barH} fill="transparent" className="drag-handle-move"
                                        onMouseDown={function (e) { handleDragStart(e, task, 'move'); }} />
                                    <rect x={x} y={barY} width={5} height={barH} fill="transparent" className="drag-handle-left"
                                        onMouseDown={function (e) { handleDragStart(e, task, 'resize-start'); }} />
                                    <rect x={x + Math.max(w, 4) - 5} y={barY} width={5} height={barH} fill="transparent" className="drag-handle-right"
                                        onMouseDown={function (e) { handleDragStart(e, task, 'resize-end'); }} />
                                </React.Fragment>
                            )}
                        </g>
                    );
                })}
            </svg>

            {/* Tooltip popup */}
            {tooltip && (
                <GanttTooltip
                    task={tooltip.task} x={tooltip.x} y={tooltip.y}
                    statusColors={statusColors}
                    startField={startField} endField={endField}
                    progressField={progressField} assigneeField={assigneeField} statusField={statusField}
                />
            )}
        </div>
    );
}
